'use client'

import * as React from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pillBtnBase } from '@/components/roostr'
import { useI18n } from '@/lib/i18n'

// AiSummaryOnDemand + AiSummaryPanel — design detail.jsx:28-54 and
// cards.jsx:245-256. Idle: primary-container "Summarize with AI" pill
// (replaces the old "Just the Facts" text button; gating handled by the
// caller's onSummarize). Done: surface-low panel with AI SUMMARY microlabel,
// a body sized for actual reading (≥ the full description's 14px — user
// readability fix 2026-07-15) and the italic disclaimer. `onHide` keeps
// production's clear-summary affordance.
export interface AiSummaryBlockProps {
  summaryText: string | null
  isSummarizing: boolean
  onSummarize: () => void
  onHide: () => void
}

// The rendered summary panel on its own — shared between the detail page's
// in-flow block below and the ReviewCard's "Summary" popover
// (design cards.jsx:245-256; the popover overrides the surface via className).
export function AiSummaryPanel({
  summaryText,
  onHide,
  className,
}: {
  summaryText: string
  onHide?: () => void
  className?: string
}) {
  const { t } = useI18n()
  return (
    <div className={cn('animate-pop-in rounded-shape-sm bg-surface-low px-3 py-[10px]', className)}>
      <div className="mb-1 flex items-center gap-[5px]">
        <Sparkles className="h-[13px] w-[13px] text-primary" />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.6px] text-ink-muted">
          {t('listingDetail.aiSummary', 'AI summary')}
        </span>
        {onHide && (
          <button
            type="button"
            onClick={onHide}
            className="ml-auto text-[10.5px] font-semibold text-ink-muted transition-colors hover:text-ink"
          >
            {t('listingDetail.hideSummary', 'Hide')}
          </button>
        )}
      </div>
      <p className="text-[14.5px] leading-[1.65] text-ink">{summaryText}</p>
      <p className="mt-[6px] text-[11.5px] italic text-ink-muted">
        {t('listingDetail.aiSummaryDisclaimer', 'AI-generated from the listing text — may not be accurate.')}
      </p>
    </div>
  )
}

export function AiSummaryBlock({ summaryText, isSummarizing, onSummarize, onHide }: AiSummaryBlockProps) {
  const { t } = useI18n()

  if (summaryText && !isSummarizing) {
    return <AiSummaryPanel summaryText={summaryText} onHide={onHide} />
  }

  return (
    <button
      type="button"
      disabled={isSummarizing}
      onClick={onSummarize}
      title={t('listingDetail.summarizeAiTitle', 'Generate an AI summary of the listing description')}
      className={cn(
        pillBtnBase,
        'bg-primary-container px-4 py-[9px] text-[13px] text-on-primary-container',
        isSummarizing && 'opacity-75'
      )}
    >
      {isSummarizing ? (
        <Loader2 className="h-[15px] w-[15px] animate-spin" />
      ) : (
        <Sparkles className="h-[15px] w-[15px]" />
      )}
      {isSummarizing ? t('listingDetail.summarizing', 'Summarizing...') : t('listingDetail.summarizeAi', 'Summarize with AI')}
    </button>
  )
}
